import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Menu,
  ListItemIcon,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
  CircularProgress,
  Radio,
} from '@mui/material';
import { 
  Download, 
  Print, 
  Refresh, 
  Search as SearchIcon,
  BookOnline as BookOnlineIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Clear as ClearIcon,
  Visibility,
  Close,
  Person,
  Home,
  CalendarToday,
  AccountBalance,
  Close as CloseIcon,
  FilterAlt,
  DateRange,
  Today,
  CalendarMonth,
  FileDownload,
} from '@mui/icons-material';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import FormatBadge from '../../components/FormatBadge/FormatBadge';
import { showSnackbar } from '../../components/Snackbar';
import { exportAsPDF, exportAsCSV, exportAsExcel } from '../../utils/importExport';
import AlertModal from '../../components/AlertModal';
import MoreFilters from '../../components/MoreFilters/MoreFilters';

import { getAssociates } from '../../services/associate';
import { getBookingReports } from '../../services/reports';

const BookingReport = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    status: '',
    associateId: '',
    searchTerm: '',
    inventoryType: '',
    inventoryDetail: '',
    paymentPlan: '',
  });

  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState('bookingDateDesc');
  const [tempSortBy, setTempSortBy] = useState('bookingDateDesc');
  const [dateFilter, setDateFilter] = useState(null);
  const [tempDateFilter, setTempDateFilter] = useState(null);

  const [tempFromDate, setTempFromDate] = useState('');
  const [tempToDate, setTempToDate] = useState('');

  const [tempInventoryType, setTempInventoryType] = useState('');
  const [tempInventoryDetail, setTempInventoryDetail] = useState('');
  const [tempPaymentPlan, setTempPaymentPlan] = useState('');

  useEffect(() => {
    if (moreFiltersOpen) {
      setTempSortBy(sortBy);
      setTempDateFilter(dateFilter);
      setTempFromDate(filters.fromDate || '');
      setTempToDate(filters.toDate || '');
      setTempInventoryType(filters.inventoryType || '');
      setTempInventoryDetail(filters.inventoryDetail || '');
      setTempPaymentPlan(filters.paymentPlan || '');
    }
  }, [moreFiltersOpen, sortBy, dateFilter, filters]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [associates, setAssociates] = useState([]);
  const [associatesLoading, setAssociatesLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [totalBookings, setTotalBookings] = useState(0);
  const [bookingsLoading, setBookingsLoading] = useState(false);

  // Export Dialog States
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState('');
  const [exportDateMode, setExportDateMode] = useState('current');
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const closeAlert = () => setAlertModal({ open: false, title: 'Alert', message: '' });


  // Associate States aligned with CancelReport
  const [associateSearchInput, setAssociateSearchInput] = useState('');
  const associateSearchTimeoutRef = useRef(null);

  const mockAssociates = [
    { id: 'AS001', name: 'Rajesh Kumar', email: 'rajesh@company.com' },
    { id: 'AS002', name: 'Priya Sharma', email: 'priya@company.com' },
    { id: 'AS003', name: 'Vikram Singh', email: 'vikram@company.com' },
    { id: 'AS004', name: 'Amit Sharma', email: 'amit@company.com' },
    { id: 'AS005', name: 'Sneha Patel', email: 'sneha@company.com' },
    { id: 'AS006', name: 'Rahul Verma', email: 'rahul@company.com' },
    { id: 'AS007', name: 'Neha Kumar', email: 'neha@company.com' },
  ];

  const sampleData = [
    {
      sno: 1,
      bookingId: 'BK001',
      bookingDate: '2025-01-01',
      customerName: 'Amit Gupta',
      plotNo: 'P101',
      plotAmount: 500000,
      paidAmount: 100000,
      dueAmount: 400000,
      status: 'Booked',
      associate: 'Rajesh Kumar',
    },
    {
      sno: 2,
      bookingId: 'BK002',
      bookingDate: '2025-01-02',
      customerName: 'Jane Smith',
      plotNo: 'P102',
      plotAmount: 750000,
      paidAmount: 750000,
      dueAmount: 0,
      status: 'Alloted',
      associate: 'Priya Sharma',
    },
    {
      sno: 3,
      bookingId: 'BK003',
      bookingDate: '2025-01-03',
      customerName: 'Mike Johnson',
      plotNo: 'P103',
      plotAmount: 600000,
      paidAmount: 150000,
      dueAmount: 450000,
      status: 'Booked',
      associate: 'Vikram Singh',
    },
  ];

  const fetchAssociatesData = async (search = '') => {
    setAssociatesLoading(true);
    try {
      const response = await getAssociates({
        page: 1,
        limit: 10,
        searchTerm: search,
        search: search,
        searchKey: search,
        filterKey: search
      });

      let associatesData = [];
      if (response && response.data) {
        associatesData = Array.isArray(response.data) ? response.data : (response.data.data || response.data.docs || []);
      } else if (Array.isArray(response)) {
        associatesData = response;
      }

      const formatted = associatesData.map(associate => ({
        id: associate._id || associate.id || associate.associateId || associate.code,
        name: associate.name || associate.fullName || associate.firstName + ' ' + (associate.lastName || ''),
        email: associate.email || associate.emailAddress || '',
        phone: associate.phone || associate.mobile || associate.whatsapp || '',
        associateId: associate.associateId || associate.id || associate._id,
      }));

      setAssociates(formatted);
    } catch (error) {
      console.error("Error fetching associates:", error);
      setAssociates([]);
    } finally {
      setAssociatesLoading(false);
    }
  };

  useEffect(() => {
    fetchAssociatesData();
  }, []);

  useEffect(() => {
    if (!hasSearched && !searchLoading && !associatesLoading) {
      handleSearch();
    }
  }, [hasSearched, searchLoading, associatesLoading]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (hasSearched) {
      handleSearch(newPage, rowsPerPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    if (hasSearched) {
      handleSearch(0, newLimit);
    }
  };

  const handleClearSearch = () => {
    setFilters({ ...filters, searchTerm: '' });
    setHasSearched(false);
    setPage(0);
  };

  const handleSearch = useCallback(async (p = page, limit = rowsPerPage, overrides = {}) => {
    setSearchLoading(true);
    setBookingsLoading(true);
    setHasSearched(true);

    try {
      const apiParams = {
        page: p + 1,
        limit: limit,
      };

      const {
        fromDate: oFromDate,
        toDate: oToDate,
        status: oStatus,
        associateId: oAssociateId,
        searchTerm: oSearchTerm,
        inventoryType: oInventoryType,
        inventoryDetail: oInventoryDetail,
        paymentPlan: oPaymentPlan,
        sortBy: oSortBy,
        dateFilter: oDateFilter,
      } = overrides;

      const effFromDate = oFromDate !== undefined ? oFromDate : filters.fromDate;
      const effToDate = oToDate !== undefined ? oToDate : filters.toDate;
      const effStatus = oStatus !== undefined ? oStatus : filters.status;
      const effAssociateId = oAssociateId !== undefined ? oAssociateId : filters.associateId;
      const effSearchTerm = oSearchTerm !== undefined ? oSearchTerm : filters.searchTerm;
      const effInventoryType = oInventoryType !== undefined ? oInventoryType : filters.inventoryType;
      const effInventoryDetail = oInventoryDetail !== undefined ? oInventoryDetail : filters.inventoryDetail;
      const effPaymentPlan = oPaymentPlan !== undefined ? oPaymentPlan : filters.paymentPlan;
      const effSortBy = oSortBy !== undefined ? oSortBy : sortBy;
      const effDateFilter = oDateFilter !== undefined ? oDateFilter : dateFilter;

      if (effFromDate) {
        apiParams.fromDate = effFromDate;
      }
      if (effToDate) {
        apiParams.toDate = effToDate;
      }
      if (effDateFilter) {
        apiParams.date = effDateFilter;
      }
      if (effStatus) {
        apiParams.status = effStatus.toLowerCase();
      }
      if (effAssociateId) {
        apiParams.associateId = effAssociateId;
      }
      if (effSearchTerm) {
        apiParams.searchKey = effSearchTerm;
      }
      if (effInventoryType) {
        apiParams.inventoryType = effInventoryType;
      }
      if (effInventoryDetail) {
        apiParams.inventoryDetail = effInventoryDetail;
      }
      if (effPaymentPlan) {
        apiParams.paymentPlan = effPaymentPlan;
      }

      let apiSortBy = 'propertyAmount';
      let apiSortOrder = 'asc';
      if (effSortBy) {
        if (effSortBy.endsWith('Desc')) {
          apiSortBy = effSortBy.replace('Desc', '');
          apiSortOrder = 'desc';
        } else if (effSortBy.endsWith('Asc')) {
          apiSortBy = effSortBy.replace('Asc', '');
          apiSortOrder = 'asc';
        }
      }
      apiParams.sortBy = apiSortBy;
      apiParams.sortOrder = apiSortOrder;

      const response = await getBookingReports(apiParams);

      let bookingsData = [];
      let totalCount = 0;

      if (response && response.data && Array.isArray(response.data.docs)) {
        bookingsData = response.data.docs;
        totalCount = response.data.totalDocs || 0;
      } else if (response && Array.isArray(response.data)) {
        bookingsData = response.data;
        totalCount = response.total || response.totalCount || response.data.length;
      } else if (Array.isArray(response)) {
        bookingsData = response;
        totalCount = response.length;
      }

      const transformedBookings = bookingsData.map((booking, index) => {
        const rawDate = booking.bookingDate || '';
        const formattedDate = rawDate ? new Date(rawDate).toLocaleDateString() : '';

        return {
          sno: (p * limit) + index + 1,
          bookingId: booking.bookingId || booking.id || `BK${String((p * limit) + index + 1).padStart(3, '0')}`,
          bookingDate: formattedDate,
          customerName: booking.customer?.name || '',
          inventoryType: booking.particular?.inventoryType || '',
          plotNo: booking.particular?.inventoryDetail || '',
          plotAmount: booking.propertyAmount || booking.totalAmount || 0,
          discountAmount: booking.discount || booking.discountAmount || 0,
          authorityCharge: booking.authorityCharge || 0,
          paidAmount: booking.totalReceived || 0,
          dueAmount: booking.totalDueAmount || 0,
          excludeVdatotalDueAmount: booking.excludeVdatotalDueAmount || 0,
          status: booking.approvedStatus || 'pending',
          associate: booking.associate?.name || '',
          originalData: booking,
        };
      });

      setBookings(transformedBookings);
      setTotalBookings(totalCount);

    } catch (error) {
      setBookings([]);
      setTotalBookings(0);
    } finally {
      setSearchLoading(false);
      setBookingsLoading(false);
    }
  }, [page, rowsPerPage, filters, selectedAssociate, sortBy, dateFilter]);

  const handleApplyFilters = () => {
    setSortBy(tempSortBy);
    setDateFilter(tempDateFilter);
    const updatedFilters = {
      ...filters,
      fromDate: tempFromDate,
      toDate: tempToDate,
      inventoryType: tempInventoryType,
      inventoryDetail: tempInventoryDetail,
      paymentPlan: tempPaymentPlan,
    };
    setFilters(updatedFilters);
    setPage(0);
    setMoreFiltersOpen(false);
    handleSearch(0, rowsPerPage, {
      ...updatedFilters,
      sortBy: tempSortBy,
      dateFilter: tempDateFilter,
    });
  };

  const handleResetFilters = () => {
    setSortBy('bookingDateDesc');
    setTempSortBy('bookingDateDesc');
    setDateFilter(null);
    setTempDateFilter(null);
    setTempFromDate('');
    setTempToDate('');
    setTempInventoryType('');
    setTempInventoryDetail('');
    setTempPaymentPlan('');
    const cleared = {
      ...filters,
      fromDate: '',
      toDate: '',
      inventoryType: '',
      inventoryDetail: '',
      paymentPlan: '',
    };
    setFilters(cleared);
    setPage(0);
    handleSearch(0, rowsPerPage, {
      ...cleared,
      sortBy: 'bookingDateDesc',
      dateFilter: null,
    });
  };

  const buildExportRows = () => {
    return bookings.map(booking => ({
      'S.No': booking.sno,
      'Booking ID': booking.bookingId,
      'Booking Date': booking.bookingDate,
      'Customer Name': booking.customerName,
      'Inventory Type': booking.inventoryType || '',
      'Plot No': booking.plotNo,
      'Plot Amount': booking.plotAmount || 0,
      'Paid Amount': booking.paidAmount || 0,
      'Due Amount': booking.dueAmount || 0,
      'Exclude VDA Total Due Amount': booking.excludeVdatotalDueAmount || 0,
      'Status': booking.status,
      'Associate': booking.associate,
    }));
  };

  const handleOpenExportDialog = (type) => {
    setExportType(type);
    setExportDialogOpen(true);
    setExportFromDate(filters.fromDate || '');
    setExportToDate(filters.toDate || '');
    setExportDateMode('current');
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
    setExportType('');
    setExportLoading(false);
  };

  const handleConfirmExport = async () => {
    setExportLoading(true);
    try {
      const apiParams = {
        export: 'true',
      };

      if (filters.status) {
        apiParams.status = filters.status.toLowerCase();
      }
      if (filters.associateId) {
        apiParams.associateId = filters.associateId;
      }
      if (filters.searchTerm) {
        apiParams.searchKey = filters.searchTerm;
      }
      if (filters.inventoryType) {
        apiParams.inventoryType = filters.inventoryType;
      }
      if (filters.inventoryDetail) {
        apiParams.inventoryDetail = filters.inventoryDetail;
      }
      if (filters.paymentPlan) {
        apiParams.paymentPlan = filters.paymentPlan;
      }

      let apiSortBy = 'propertyAmount';
      let apiSortOrder = 'asc';
      if (sortBy) {
        if (sortBy.endsWith('Desc')) {
          apiSortBy = sortBy.replace('Desc', '');
          apiSortOrder = 'desc';
        } else if (sortBy.endsWith('Asc')) {
          apiSortBy = sortBy.replace('Asc', '');
          apiSortOrder = 'asc';
        }
      }
      apiParams.sortBy = apiSortBy;
      apiParams.sortOrder = apiSortOrder;

      // Apply filters only if NOT in "All Dates (Full History)" mode
      if (exportDateMode !== 'all') {
        if (exportDateMode === 'current') {
          if (filters.fromDate) apiParams.fromDate = filters.fromDate;
          if (filters.toDate) apiParams.toDate = filters.toDate;
          if (dateFilter) {
            apiParams.date = dateFilter;
          }
        } else if (exportDateMode === 'custom') {
          if (exportFromDate) apiParams.fromDate = exportFromDate;
          if (exportToDate) apiParams.toDate = exportToDate;
        } else if (exportDateMode === 'single') {
          if (exportFromDate) {
            apiParams.date = exportFromDate;
          }
        }
      }

      const response = await getBookingReports(apiParams);

      let bookingsData = [];
      if (response && response.data && Array.isArray(response.data.docs)) {
        bookingsData = response.data.docs;
      } else if (response && Array.isArray(response.data)) {
        bookingsData = response.data;
      } else if (Array.isArray(response)) {
        bookingsData = response;
      }

      if (bookingsData.length === 0) {
        setAlertModal({ open: true, title: 'Warning', message: 'No data found for the selected filter/date range.' });
        setExportLoading(false);
        return;
      }

      const transformedBookings = bookingsData.map((booking, index) => {
        const rawDate = booking.bookingDate || '';
        const formattedDate = rawDate ? new Date(rawDate).toLocaleDateString() : '';

        return {
          sno: index + 1,
          bookingId: booking.bookingId || booking.id || `BK${String(index + 1).padStart(3, '0')}`,
          bookingDate: formattedDate,
          customerName: booking.customer?.name || '',
          inventoryType: booking.particular?.inventoryType || '',
          plotNo: booking.particular?.inventoryDetail || '',
          plotAmount: booking.propertyAmount || booking.totalAmount || 0,
          paidAmount: booking.totalReceived || 0,
          dueAmount: booking.totalDueAmount || 0,
          excludeVdatotalDueAmount: booking.excludeVdatotalDueAmount || 0,
          status: booking.approvedStatus || 'pending',
          associate: booking.associate?.name || '',
        };
      });

      const rows = transformedBookings.map(booking => ({
        'S.No': booking.sno,
        'Booking ID': booking.bookingId,
        'Booking Date': booking.bookingDate,
        'Customer Name': booking.customerName,
        'Inventory Type': booking.inventoryType || '',
        'Plot No': booking.plotNo,
        'Plot Amount': booking.plotAmount || 0,
        'Paid Amount': booking.paidAmount || 0,
        'Due Amount': booking.dueAmount || 0,
        'Exclude VDA Total Due Amount': booking.excludeVdatotalDueAmount || 0,
        'Status': booking.status,
        'Associate': booking.associate,
      }));

      const fileName = 'booking_report';

      if (exportType === 'csv') {
        exportAsCSV({ rows, fileNameBase: fileName });
      } else if (exportType === 'pdf') {
        const formattedRows = rows.map(r => ({
          ...r,
          'Plot Amount': `Rs. ${r['Plot Amount'].toLocaleString('en-IN')}`,
          'Paid Amount': `Rs. ${r['Paid Amount'].toLocaleString('en-IN')}`,
          'Due Amount': `Rs. ${r['Due Amount'].toLocaleString('en-IN')}`,
          'Exclude VDA Total Due Amount': `Rs. ${r['Exclude VDA Total Due Amount'].toLocaleString('en-IN')}`,
        }));
        exportAsPDF({ rows: formattedRows, title: 'Booking Report', fileNameBase: fileName, orientation: 'l' });
      } else if (exportType === 'excel') {
        exportAsExcel({ rows, sheetName: 'Booking Report', fileNameBase: fileName });
      }

      handleCloseExportDialog();
    } catch (error) {
      console.error('Export failed:', error);
      showSnackbar({ type: 'error', message: 'Export failed. Please try again.' });
    } finally {
      setExportLoading(false);
    }
  };

  const handleView = (booking) => {
    setSelectedBooking(booking);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedBooking(null);
  };

  const getFilteredData = useCallback(() => {
    return bookings;
  }, [bookings]);

  const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);

  const displayTotalBookings = hasSearched ? totalBookings : 0;

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Booking Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track and manage plot bookings
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
            <ExportButton
              onExportPdf={() => handleOpenExportDialog('pdf')}
              onExportCsv={() => handleOpenExportDialog('csv')}
              onExportExcel={() => handleOpenExportDialog('excel')}
            />
            <RefreshButton
              onClick={() => handleSearch(page, rowsPerPage)}
              loading={searchLoading || bookingsLoading}
              sx={{
                fontWeight: 600,
                px: 2,
                fontSize: '15px',
                borderRadius: 2,
                height: { xs: 'auto', sm: 40 }
              }}
            />
          </Box>
        </Box>
      </Box>


      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', mb: 3 }}>
            Booking Filters
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select 
                  label="Status" 
                  value={filters.status}
                  onChange={(e) => {
                    setFilters({ ...filters, status: e.target.value });
                  }}
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="Booked">Booked</MenuItem>
                  <MenuItem value="Alloted">Alloted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4.5}>
              <Autocomplete
                size="small"
                options={associates}
                filterOptions={(x) => x}
                loading={associatesLoading}
                getOptionLabel={(opt) => {
                  if (!opt) return '';
                  if (typeof opt === 'string') return opt;
                  const name = opt.name || 'N/A';
                  const mobile = opt.phone || opt.mobile || opt.whatsapp || '';
                  return mobile ? `${name} — ${mobile}` : `${name}`;
                }}
                value={selectedAssociate}
                inputValue={associateSearchInput}
                onInputChange={(event, newInputValue, reason) => {
                  if (event && event.type === 'blur') return;
                  setAssociateSearchInput(newInputValue);
                  if (reason === 'reset') return;

                  if (associateSearchTimeoutRef.current) {
                    clearTimeout(associateSearchTimeoutRef.current);
                  }
                  associateSearchTimeoutRef.current = setTimeout(() => {
                    fetchAssociatesData(newInputValue);
                  }, 500);
                }}
                onChange={(_, newValue) => {
                  setSelectedAssociate(newValue);
                  setFilters({ ...filters, associateId: newValue ? newValue.id : '' });
                  setAssociateSearchInput(newValue ? (newValue.phone ? `${newValue.name} — ${newValue.phone}` : newValue.name) : "");
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return (option.id || option._id) === (value.id || value._id);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Associate"
                    placeholder="Search associate..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {associatesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.name || 'N/A'}
                      </Typography>
                      {(option.phone || option.email) && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', gap: 0.5 }}>
                          {option.phone || option.email}
                          {option.associateId && ` • ID: ${option.associateId}`}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                noOptionsText={associatesLoading ? "Loading associates..." : "No associates found"}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={1.2}>
              <Tooltip title="Apply all filters and search" arrow>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleSearch()}
                  disabled={searchLoading}
                  startIcon={searchLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: '#536DFE',
                    borderRadius: 2,
                    height: '40px',
                    '&:hover': {
                      backgroundColor: '#3F51B5',
                    },
                    '&:disabled': {
                      backgroundColor: '#e0e0e0',
                      color: '#9e9e9e',
                    }
                  }}
                >
                  {searchLoading ? 'Searching...' : 'Search'}
                </Button>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={4} md={1.1}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setFilters({
                    fromDate: '',
                    toDate: '',
                    status: '',
                    associateId: '',
                    searchTerm: '',
                    inventoryType: '',
                    inventoryDetail: '',
                    paymentPlan: '',
                  });
                  setSortBy('bookingDateDesc');
                  setTempSortBy('bookingDateDesc');
                  setDateFilter(null);
                  setTempDateFilter(null);
                  setTempFromDate('');
                  setTempToDate('');
                  setTempInventoryType('');
                  setTempInventoryDetail('');
                  setTempPaymentPlan('');
                  setHasSearched(false);
                  setSearchLoading(false);
                  setSelectedAssociate(null);
                  setPage(0);
                  handleSearch(0, rowsPerPage, {
                    fromDate: '',
                    toDate: '',
                    status: '',
                    associateId: '',
                    searchTerm: '',
                    inventoryType: '',
                    inventoryDetail: '',
                    paymentPlan: '',
                    sortBy: 'bookingDateDesc',
                    dateFilter: null,
                  });
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#536DFE',
                  color: '#536DFE',
                  borderRadius: 2,
                  height: '40px',
                  '&:hover': {
                    borderColor: '#3F51B5',
                    backgroundColor: '#f0f7ff',
                  }
                }}
              >
                Reset
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} md={1.7}>
              <Box sx={{ width: '100%' }}>
                <MoreFilters
                  expanded={moreFiltersOpen}
                  onToggle={() => setMoreFiltersOpen(!moreFiltersOpen)}
                  sortBy={tempSortBy}
                  onChangeSortBy={setTempSortBy}
                  onApply={handleApplyFilters}
                  onReset={handleResetFilters}
                  type="bookingReport"
                  height="40px"
                >
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="From Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={tempFromDate}
                        onChange={(e) => {
                          const val = e.target.value;
                          setTempFromDate(val);
                          if (val) {
                            setTempDateFilter(null);
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="To Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={tempToDate}
                        onChange={(e) => {
                          const val = e.target.value;
                          setTempToDate(val);
                          if (val) {
                            setTempDateFilter(null);
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Search by Single Day"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={tempDateFilter || ''}
                        onChange={(e) => {
                          const val = e.target.value || null;
                          setTempDateFilter(val);
                          if (val) {
                            setTempFromDate('');
                            setTempToDate('');
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Inventory Type</InputLabel>
                        <Select
                          label="Inventory Type"
                          value={tempInventoryType}
                          onChange={(e) => setTempInventoryType(e.target.value)}
                        >
                          <MenuItem value="">All Types</MenuItem>
                          <MenuItem value="Plot">Plot</MenuItem>
                          <MenuItem value="Apartment">Apartment</MenuItem>
                          <MenuItem value="RowHouse">RowHouse</MenuItem>
                          <MenuItem value="Commercial">Commercial</MenuItem>
                          <MenuItem value="Villa">Villa</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Property No"
                        placeholder="e.g. RH-1"
                        value={tempInventoryDetail}
                        onChange={(e) => setTempInventoryDetail(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Payment Plan</InputLabel>
                        <Select
                          label="Payment Plan"
                          value={tempPaymentPlan}
                          onChange={(e) => setTempPaymentPlan(e.target.value)}
                        >
                          <MenuItem value="">All Plans</MenuItem>
                          <MenuItem value="EMI">EMI</MenuItem>
                          <MenuItem value="Full Payment">Full Payment</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </MoreFilters>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
     


      {/* Search Results Summary */}


      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Booking Details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              {hasSearched ? `${displayTotalBookings} booking${displayTotalBookings !== 1 ? 's' : ''} found` : 'Use filters above and click Search to view bookings'}
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Booking ID</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Booking Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Property Type</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Property No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Property Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Paid Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Include VDA Due Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Exclude VDA Due Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Associate</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(searchLoading || bookingsLoading) ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          {searchLoading ? 'Searching bookings...' : 'Loading bookings...'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : !hasSearched ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 6 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <BookOnlineIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                          Ready to View Bookings
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Apply filters above and click Search to view booking reports
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length > 0 ? (
                  filteredData.map((row) => (
                  <TableRow 
                    key={row.sno} 
                    hover
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                      '&:hover': { backgroundColor: '#f0f7ff !important' }
                    }}
                  >
                    <TableCell>{row.sno}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.bookingId}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.bookingDate}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.inventoryType}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.plotNo}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Rs. {row.plotAmount.toLocaleString('en-IN')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2e7d32' }}>Rs. {row.paidAmount.toLocaleString('en-IN')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: row.dueAmount > 0 ? '#d32f2f' : '#2e7d32' }}>
                      Rs. {row.dueAmount.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: (row.excludeVdatotalDueAmount || 0) > 0 ? '#d32f2f' : '#2e7d32' }}>
                      Rs. {(row.excludeVdatotalDueAmount || 0).toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        size="small"
                        color={row.status === 'approved' ? 'success' : row.status === 'booked' ? 'primary' : 'warning'}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>{row.associate}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleView(row)}
                          sx={{ color: '#536DFE' }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No bookings found matching your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {hasSearched && displayTotalBookings > 0 && (
            <TablePagination
              
              component="div"
              count={displayTotalBookings}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: '#536DFE',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility />
            <Typography variant="h6" fontWeight="bold">
              Booking Details
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseViewDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedBooking && (
            <>
              {/* Header Section */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 3,
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Avatar sx={{ bgcolor: '#536DFE', width: 48, height: 48 }}>
                  <Person />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedBooking.customerName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Booking ID: {selectedBooking.bookingId}
                  </Typography>
                  <Chip 
                    label={selectedBooking.status} 
                    size="small" 
                    color={selectedBooking.status === 'Alloted' ? 'success' : 'primary'}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>

              <Grid container spacing={3}>
                {/* Property Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Home color="primary" />
                        Property Details
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Plot Number:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedBooking.plotNo}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Booking Date:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedBooking.bookingDate}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Associate:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedBooking.associate}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Financial Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountBalance color="primary" />
                        Financial Details
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Plot Amount:</Typography>
                          <Typography variant="body2" fontWeight="600">₹{selectedBooking.plotAmount.toLocaleString('en-IN')}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Discount:</Typography>
                          <Typography variant="body2" fontWeight="600" color="info.main">₹{(selectedBooking.discountAmount || 0).toLocaleString('en-IN')}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Auth. Charge:</Typography>
                          <Typography variant="body2" fontWeight="600" color="warning.main">₹{(selectedBooking.authorityCharge || 0).toLocaleString('en-IN')}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Paid Amount:</Typography>
                          <Typography variant="body2" fontWeight="600" color="success.main">₹{selectedBooking.paidAmount.toLocaleString('en-IN')}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Due Amount:</Typography>
                          <Typography variant="body2" fontWeight="600" color={selectedBooking.dueAmount > 0 ? 'error.main' : 'success.main'}>
                            ₹{selectedBooking.dueAmount.toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Additional Information */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday color="primary" />
                    Additional Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          {((selectedBooking.paidAmount / selectedBooking.plotAmount) * 100).toFixed(1)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Payment Progress</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#e8f5e8', borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight="bold" color="success.main">
                          ₹{selectedBooking.paidAmount.toLocaleString('en-IN')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Total Paid</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: selectedBooking.dueAmount > 0 ? '#ffeaea' : '#e8f5e8', borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight="bold" color={selectedBooking.dueAmount > 0 ? 'error.main' : 'success.main'}>
                          ₹{selectedBooking.dueAmount.toLocaleString('en-IN')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Remaining Amount</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button 
            onClick={handleCloseViewDialog}
            variant="contained"
            sx={{ 
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Date Range Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 16px 48px rgba(83, 109, 254, 0.15), 0 2px 12px rgba(0,0,0,0.06)',
          }
        }}
      >
        {/* Gradient Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 50%, #304ffe 100%)',
          px: 2.5, py: 1.8,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -15, right: -15, width: 55, height: 55, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FileDownload sx={{ color: '#fff', fontSize: 18 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem', lineHeight: 1.2 }}>
                  Export Booking Report
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', mt: 0.2 }}>
                  Download your report data
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseExportDialog} size="small" sx={{ color: 'rgba(255,255,255,0.8)', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          {/* Format Badge */}
          <FormatBadge type={exportType} />

          {/* Section label */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Select Date Range
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {/* Option 1: Current Filters */}
            <Box
              onClick={() => setExportDateMode('current')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'current' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'current' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'current' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <FilterAlt sx={{ fontSize: 16, color: exportDateMode === 'current' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Current Filters
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {filters.fromDate && filters.toDate
                    ? `${filters.fromDate} → ${filters.toDate}`
                    : filters.fromDate ? `From ${filters.fromDate}` : filters.toDate ? `Till ${filters.toDate}` : 'All dates'}
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'current'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 2: Custom Date Range */}
            <Box
              onClick={() => setExportDateMode('custom')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'custom' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'custom' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'custom' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <DateRange sx={{ fontSize: 16, color: exportDateMode === 'custom' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Custom Date Range
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Choose from & to dates
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'custom'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 3: Single Day */}
            <Box
              onClick={() => setExportDateMode('single')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'single' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'single' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'single' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <Today sx={{ fontSize: 16, color: exportDateMode === 'single' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Single Day
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export for one specific date
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'single'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 4: All Dates (Full History) */}
            <Box
              onClick={() => setExportDateMode('all')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'all' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'all' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'all' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <CalendarMonth sx={{ fontSize: 16, color: exportDateMode === 'all' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  All Data (Full History)
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export full database history without any filters
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'all'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>
          </Box>

          {/* Date inputs — compact animated */}
          {exportDateMode === 'custom' && (
            <Box sx={{
              display: 'flex', gap: 1.5, mt: 1.5,
              p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="From Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
              <TextField
                label="To Date" type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}

          {exportDateMode === 'single' && (
            <Box sx={{
              mt: 1.5, p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="Select Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}
        </DialogContent>

        {/* Actions */}
        <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#fafbff', borderTop: '1px solid #eef2f6', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            onClick={handleCloseExportDialog}
            variant="outlined" size="small"
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              px: 2, fontSize: '0.8rem', borderColor: '#c5cae9', color: '#536DFE',
              '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83,109,254,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmExport}
            variant="contained" size="small"
            disabled={exportLoading || (exportDateMode === 'custom' && (!exportFromDate || !exportToDate)) || (exportDateMode === 'single' && !exportFromDate)}
            startIcon={exportLoading ? <CircularProgress size={14} color="inherit" /> : <FileDownload sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 700,
              px: 2.5, fontSize: '0.8rem',
              background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 100%)',
              boxShadow: '0 3px 10px rgba(83, 109, 254, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #3d5afe 0%, #304ffe 100%)', boxShadow: '0 4px 14px rgba(83, 109, 254, 0.4)' },
              '&.Mui-disabled': { background: '#e0e0e0', boxShadow: 'none' },
            }}
          >
            {exportLoading ? 'Exporting...' : 'Export Now'}
          </Button>
        </Box>
      </Dialog>
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />
    </Box>
  );
};

export default BookingReport;

